/**
 * 角色菜单分配抽屉
 * 使用 Ant Design Tree 组件展示菜单树，支持勾选分配
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Tree, Button, Space, Spin, Empty, Input } from 'antd';
import { message } from '@/utils/antdStatic';
import { SaveOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { getMenuTree } from '../../api/menu';
import { getRoleMenus, setRoleMenus } from '../../api/menu';
import { buildRoleMenuIDs, splitMenuKeysForTree } from './roleMenuTreeState';

interface RoleMenuTreeProps {
  visible: boolean;
  roleId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const convertToTreeData = (nodes: any[]): DataNode[] =>
  nodes.map((node) => ({
    key: String(node.id ?? node.key ?? node.value),
    title: node.title || node.name || '未命名',
    children: node.children?.length ? convertToTreeData(node.children) : undefined,
  }));

const collectAllKeys = (nodes: DataNode[]): string[] => {
  const keys: string[] = [];
  const traverse = (list: DataNode[]) => {
    for (const node of list) {
      keys.push(String(node.key));
      if (node.children?.length) traverse(node.children);
    }
  };
  traverse(nodes);
  return keys;
};

const RoleMenuTree: React.FC<RoleMenuTreeProps> = ({ visible, roleId, onClose, onSuccess }) => {
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [assignedMenuKeys, setAssignedMenuKeys] = useState<string[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [halfCheckedKeys, setHalfCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const fetchMenuTree = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getMenuTree();
      const data = res?.data?.data || res?.data || [];
      const items = Array.isArray(data) ? data : data.tree || data.list || [];
      const tree = convertToTreeData(items);
      setTreeData(tree);
      setExpandedKeys(collectAllKeys(tree));
    } catch {
      message.error('获取菜单树失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoleMenus = useCallback(async () => {
    if (!roleId) return;
    try {
      const res: any = await getRoleMenus(roleId);
      const data = res?.data?.data || res?.data || [];
      const menuIds = Array.isArray(data) ? data.map((id: any) => String(id)) : [];
      setAssignedMenuKeys(menuIds);
    } catch {
      setAssignedMenuKeys([]);
      setCheckedKeys([]);
      setHalfCheckedKeys([]);
    }
  }, [roleId]);

  useEffect(() => {
    if (visible && roleId) {
      setAssignedMenuKeys([]);
      setCheckedKeys([]);
      setHalfCheckedKeys([]);
      fetchMenuTree();
      fetchRoleMenus();
      setSearchValue('');
    }
  }, [visible, roleId, fetchMenuTree, fetchRoleMenus]);

  useEffect(() => {
    if (!visible || treeData.length === 0) return;
    const splitKeys = splitMenuKeysForTree(assignedMenuKeys, treeData);
    setCheckedKeys(splitKeys.checkedKeys);
    setHalfCheckedKeys(splitKeys.halfCheckedKeys);
  }, [visible, assignedMenuKeys, treeData]);

  const handleCheck = (checked: any, info?: any) => {
    const nextCheckedKeys = Array.isArray(checked) ? checked : checked?.checked || [];
    const nextHalfCheckedKeys = Array.isArray(checked) ? info?.halfCheckedKeys || [] : checked?.halfChecked || [];

    setCheckedKeys(nextCheckedKeys.map((key: any) => String(key)));
    setHalfCheckedKeys(nextHalfCheckedKeys.map((key: any) => String(key)));
  };

  const handleSave = async () => {
    if (!roleId) return;
    setSaving(true);
    try {
      await setRoleMenus(roleId, buildRoleMenuIDs(checkedKeys, halfCheckedKeys));
      message.success('菜单分配成功');
      onSuccess();
    } catch {
      message.error('菜单分配失败');
    } finally {
      setSaving(false);
    }
  };

  const highlightTreeData = (nodes: DataNode[], keyword: string): DataNode[] => {
    if (!keyword) return nodes;
    return nodes.map((node) => {
      const title = String(node.title || '');
      const idx = title.toLowerCase().indexOf(keyword.toLowerCase());
      const highlighted = idx > -1 ? (
        <span>
          {title.substring(0, idx)}
          <span style={{ color: '#f50', fontWeight: 'bold' }}>{title.substring(idx, idx + keyword.length)}</span>
          {title.substring(idx + keyword.length)}
        </span>
      ) : <span>{title}</span>;
      return { ...node, title: highlighted, children: node.children ? highlightTreeData(node.children, keyword) : undefined };
    });
  };

  return (
    <Drawer
      title="分配菜单"
      placement="right"
      width={480}
      open={visible}
      onClose={onClose}
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Input.Search
          placeholder="搜索菜单名称"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ marginBottom: 12 }}
          allowClear
        />
        <Space style={{ marginBottom: 12 }} wrap>
          <Button size="small" onClick={() => setExpandedKeys(collectAllKeys(treeData))}>展开全部</Button>
          <Button size="small" onClick={() => setExpandedKeys([])}>收起全部</Button>
          <Button size="small" onClick={() => { setCheckedKeys(collectAllKeys(treeData)); setHalfCheckedKeys([]); }}>全选</Button>
          <Button size="small" onClick={() => { setCheckedKeys([]); setHalfCheckedKeys([]); }}>取消全选</Button>
        </Space>
        {treeData.length > 0 ? (
          <Tree
            checkable
            checkStrictly={false}
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            onCheck={handleCheck}
            onExpand={(keys) => setExpandedKeys(keys as string[])}
            treeData={searchValue ? highlightTreeData(treeData, searchValue) : treeData}
            height={500}
            style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}
          />
        ) : (
          !loading && <Empty description="暂无菜单数据" />
        )}
        <div style={{ marginTop: 16, color: '#999', fontSize: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          已选菜单: {checkedKeys.length} 项
          {halfCheckedKeys.length > 0 && `（含 ${halfCheckedKeys.length} 个半选父节点）`}
        </div>
      </Spin>
    </Drawer>
  );
};

export default RoleMenuTree;
